import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Alert, Button, Card, setSubmitError, toast, useAppForm } from '@keboola/design';

import { api } from '../../api';
import { TextLink } from '../../components/TextLink';
import { getApiErrorMessage } from '../../features/auth/errors';
import { resetConfirmFormSchema, resetRequestFormSchema } from '../../features/auth/schemas';
import { formValidators } from '../../features/auth/validate';

export const Route = createFileRoute('/auth/reset-password')({
  component: ResetPasswordPage,
  head: () => ({ meta: [{ title: 'Reset your password | Keboola Developer Portal' }] }),
});

/**
 * Two phases on one route, as in the original: request a code, then redeem it.
 *
 * The email is held in component state between them rather than put in the URL — apps-api takes it as a
 * **path segment** on both calls, and a password-reset URL carrying an address is the sort of thing that
 * ends up in a shared link or a referrer header.
 */
function ResetPasswordPage() {
  const [emailAwaitingCode, setEmailAwaitingCode] = useState<string | null>(null);

  return emailAwaitingCode === null ? (
    <RequestPhase onSent={setEmailAwaitingCode} />
  ) : (
    <ConfirmPhase email={emailAwaitingCode} onRestart={() => setEmailAwaitingCode(null)} />
  );
}

function RequestPhase({ onSent }: { onSent: (email: string) => void }) {
  const form = useAppForm({
    defaultValues: { email: '' },
    validators: formValidators(resetRequestFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await api.auth.requestPasswordReset(value.email);
        onSent(value.email);
      } catch (error) {
        setSubmitError(
          formApi,
          getApiErrorMessage(error, 'There was a problem sending the reset code.'),
        );
      }
    },
  });

  return (
    <form.AppForm>
      <form.FormBody>
        <Card.Title as="h1">Reset your password</Card.Title>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          We will email you a confirmation code.
        </p>

        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label="Email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          )}
        </form.AppField>

        <form.SubmitErrorAlert />
        <form.SubmitButton>Send code</form.SubmitButton>

        <p className="tw:m-0 tw:border-t tw:border-border tw:pt-4 tw:text-sm tw:text-muted-foreground">
          <TextLink to="/auth/login">Back to sign in</TextLink>
        </p>
      </form.FormBody>
    </form.AppForm>
  );
}

function ConfirmPhase({ email, onRestart }: { email: string; onRestart: () => void }) {
  const navigate = useNavigate();

  const form = useAppForm({
    defaultValues: { code: '', password: '', confirmPassword: '' },
    validators: formValidators(resetConfirmFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await api.auth.confirmPasswordReset(email, { code: value.code, password: value.password });
        // A toast rather than another screen: the user's next step is signing in, and the sign-in page
        // is where they are going anyway.
        toast.success('Password changed. You can sign in now.');
        await navigate({ to: '/auth/login' });
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'That code was not accepted.'));
      }
    },
  });

  return (
    <form.AppForm>
      <form.FormBody>
        <Card.Title as="h1">Choose a new password</Card.Title>
        <Alert variant="info">
          We sent a confirmation code to <strong>{email}</strong>.
        </Alert>

        <form.AppField name="code">
          {(field) => (
            <field.TextField
              label="Confirmation code"
              required
              autoComplete="one-time-code"
              placeholder="From the email we just sent"
            />
          )}
        </form.AppField>

        <form.AppField name="password">
          {(field) => (
            <field.PasswordField
              label="New password"
              required
              autoComplete="new-password"
              allowPasswordToggle
              help="At least 8 characters, with an uppercase letter, a lowercase letter and a number."
            />
          )}
        </form.AppField>

        <form.AppField name="confirmPassword">
          {(field) => (
            <field.PasswordField
              label="Confirm new password"
              required
              autoComplete="new-password"
              allowPasswordToggle
            />
          )}
        </form.AppField>

        <form.SubmitErrorAlert />
        <form.SubmitButton>Change password</form.SubmitButton>

        <Button variant="invisible" onClick={onRestart}>
          Use a different email address
        </Button>
      </form.FormBody>
    </form.AppForm>
  );
}
